import { useEffect, useState } from "react";
import { dropDown, edit, plus, trash } from "../../asset";
import { ExpandableSection } from "../../components";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import PageHeader from "../../components/PageHeader";
import QuestionForm from "./QuestionForm";
import { useLocation, useParams } from "react-router-dom";
import { ExamDetails, ExamSection } from "../../types/ExamType";
import { toast } from "sonner";
import examsApis from "../../api/ExamsApis";
import useConfirmToast from "../../util/hooks/useConfirmToast";

export default function QuestionBlock() {
  const [sections, setSections] = useState<ExamSection[]>([]);
  const [name, setName] = useState("");
  const [showInput, setShowInput] = useState(false);
  const [openForms, setOpenForms] = useState<Record<number, boolean>>({});
  const [editSectionId, setEditSectionId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");
  const { examId } = useParams<{ examId: string }>();
  const examIdNumber = Number(examId);
  const { state } = useLocation();
  const examSeqId = state?.examSeqId;
  const [exam, setExam] = useState<ExamDetails | null>(null);
  const { confirm, modal } = useConfirmToast();
  const [editingQuestion, setEditingQuestion] = useState<any | null>(null);
  const [editingSectionId, setEditingSectionId] = useState<number | null>(null);

  const fetchExam = async () => {
    if (!examSeqId) return;

    try {
      const response = await examsApis.getExamsBySeqId(examSeqId);
      if (response?.status !== 1) return;

      setExam(response.data);
      setSections(response.data.examSections || []);
    } catch (err) {
      toast.error("Failed to load exam");
    }
  };

  useEffect(() => {
    fetchExam();
  }, []);

  const addEditSection = async () => {
    if (!name.trim() || !examIdNumber) return;

    const res = await examsApis.addExamSection(examIdNumber, {
      sectionName: name,
    });

    if (res?.status === 1) {
      setName("");
      setShowInput(false);
      fetchExam(); // Refresh the sections
    }
  };

  const updateSection = async () => {
    if (!editName.trim() || !editSectionId) return;

    try {
      await examsApis.updateSection(editSectionId.toString(), editName);
      setEditSectionId(null);
      fetchExam();
    } catch (err) {
      toast.error("Failed to update section");
    }
  };

  const deleteSection = async (sectionId: number) => {
    const result = await confirm(
      "Are you sure you want to delete this section?"
    );
    if (!result) return;
    await examsApis.deleteSection(sectionId.toString());
    fetchExam();
  };

  const editSection = (id: number) => {
    const sec = sections.find((s) => s.examSectionId === id);
    if (!sec) return;

    setEditSectionId(id);
    setEditName(sec.sectionName);
  };

  const openQuestionForm = (id: number) =>
    setOpenForms((p) => ({ ...p, [id]: true }));

  const closeQuestionForm = (id: number) =>
    setOpenForms((p) => ({ ...p, [id]: false }));

  const deleteQuestion = async (questionId: number) => {
    const result = await confirm(
      "Are you sure you want to delete this Question?"
    );
    if (!result) return;
    await examsApis.deleteQuestions(questionId.toString());
    fetchExam();
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-between items-center">
        <PageHeader title={exam?.examName || "Exam"} showBack />
        <div className="flex gap-4">
          <span className="text-sm font-semibold text-neutral-400">
            Duration :
          </span>
          <div className="flex gap-2 text-error-700 text-sm font-bold">
            <p>{exam?.duration} minutes</p>
            <img src={dropDown} alt="" />
          </div>
        </div>
      </div>

      <div className="bg-white px-8 py-5 rounded-lg flex flex-col gap-3">
        <h3 className="text-lg font-semibold text-neutral-400">
          Exam Sections
        </h3>

        {modal}

        {sections.map((section, index) => (
          <ExpandableSection
            key={section.examSectionId}
            section={{
              id: section.examSectionId,
              name: section.sectionName,
            }}
            onEdit={() => editSection(section.examSectionId)}
            onDelete={() => deleteSection(section.examSectionId)}
            isFirst={index === 0}
          >
            {editSectionId === section.examSectionId ? (
              <div className="mt-4 flex flex-col gap-4">
                <CustomInput
                  name="sectionName"
                  label="Section name"
                  value={editName}
                  onChange={(e) => setEditName(e.target.value)}
                />
                <div className="flex justify-end gap-2">
                  <button
                    className="bg-primary-500 px-4 py-1 rounded-full text-white"
                    onClick={updateSection}
                  >
                    Save
                  </button>
                  <button
                    className="bg-neutral-200 px-4 py-1 rounded-full text-white"
                    onClick={() => setEditSectionId(null)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : openForms[section.examSectionId] ? (
              <QuestionForm
                sectionId={section.examSectionId}
                editQuestion={
                  editingSectionId === section.examSectionId
                    ? editingQuestion
                    : null
                }
                onSave={async () => {
                  setEditingQuestion(null);
                  setEditingSectionId(null);
                  closeQuestionForm(section.examSectionId);
                  fetchExam();
                }}
                onCancel={() => {
                  setEditingQuestion(null);
                  setEditingSectionId(null);
                  closeQuestionForm(section.examSectionId);
                }}
              />
            ) : (
              <>
                {section.examQuestions?.map((q, i) => (
                  <div
                    key={i}
                    className="p-3 rounded flex justify-between max-w-2xl items-start mb-2"
                  >
                    <div className="flex gap-1 flex-col">
                      <p className="font-semibold text-neutral-400 text-sm">
                        {q.questionName}
                      </p>
                      <p className="text-sm text-neutral-400 font-medium">
                        Marks: {q.marks}
                      </p>
                      <p className="text-neutral-200 text-xs font-medium">
                        {q.questionType}
                      </p>
                    </div>
                    <div className="flex gap-4">
                      <button onClick={(e) => {}}>
                        <img
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingQuestion(q);
                            setEditingSectionId(section.examSectionId);
                            setOpenForms((p) => ({
                              ...p,
                              [section.examSectionId]: true,
                            }));
                          }}
                          src={edit}
                          alt=""
                          className="w-4 h-4"
                        />
                      </button>
                      <button  onClick={() => deleteQuestion(q.questionId)}>
                        <img src={trash} alt="" className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}

                <div className="flex justify-end">
                  <CustomButton
                    color="bg-warning-600"
                    text="Add Question"
                    icon={plus}
                    onClick={() => openQuestionForm(section.examSectionId)}
                  />
                </div>
              </>
            )}
          </ExpandableSection>
        ))}

        {showInput && (
          <div className="flex flex-col gap-4">
            <CustomInput
              name="newSectionName"
              label="Section name"
              placeholder="Enter Section Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <div className="flex justify-end gap-2">
              <button
                className="bg-primary-500 px-4 py-1 rounded-full text-white"
                onClick={addEditSection}
              >
                Save
              </button>
              <button
                className="bg-neutral-200 px-4 py-1 rounded-full text-white"
                onClick={() => setShowInput(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <CustomButton
          color="bg-success-600"
          text="Add Section"
          icon={plus}
          onClick={() => setShowInput(true)}
        />
      </div>
    </div>
  );
}
